import {
  createProductItem,
  googleAnalyticsNormalAction,
  googleAnalyticsQuoteButton,
  googleAnalyticsWithEmailAndMobile,
  googleAnalyticsWithQuotationDetails,
} from '@/helpers/GoogleAnalytic';
import {
  capitalizeFirstChar,
  capitalizeFirstLetterOfEachWord,
} from '@/helpers/InputKeyPressValidator';
import { getItemNameGA, getProductNameGA } from '@/helpers/ProductHelper';
import { useAppSelector } from '@/hook';
import { useRouteSection } from '@/hooks/useRouteSection';
import Cookies from 'js-cookie';

import CovidAddonContent from '@/content/my/covid-addon-content';
import { CountryCode } from '@/enums';
import { getCountryCode } from '@/helpers/CountryCodeHelper';
import AdventurousActivitiesDetails from '@/locales/en_MY/addon.json';

export const isEnableGA = {
  [CountryCode.Malaysia]: true,
  [CountryCode.Philippines]: false,
};

export function useGoogleAnalytic() {
  const personalDetails = useAppSelector((state) => state.personalDetail);
  const planSummary = useAppSelector((state) => state.planSummary);
  const uiState = useAppSelector((state) => state.uiState);

  const productType = useRouteSection();
  const productName = getProductNameGA();
  const itemName = getItemNameGA();
  const countryCode = getCountryCode();

  const covidData = CovidAddonContent();
  const adventurousData = AdventurousActivitiesDetails;

  const { covid19, adventurousActivities } = planSummary.addonCoverages;
  const covidAdd = covid19.isSelected ? covidData.gaTitle : '';
  const adventurousAdd = adventurousActivities.isSelected
    ? adventurousData.label
    : '';

  const destination = planSummary?.quotationDetail?.enDestination;
  const tripType = planSummary?.quotationDetail?.enTripType;
  const planType = planSummary?.quotationDetail?.enPlanType;
  const totalPrices = planSummary.priceBreakdown?.totalPremium.toFixed(2);
  const planPrice = uiState.productPremiumRate?.find(
    (item) => item.planClass === planSummary.planClass,
  );
  const planClass = planSummary.quotationDetail?.enPlanClassType?.find(
    (item) => item.planClassId === planSummary.planClass,
  );

  const storedData = Cookies.get('myCookie');
  const myCookieArray =
    storedData !== undefined ? JSON.parse(storedData) : null;

  // #region Shared

  const handleAddon = () => {
    if (covidAdd !== '' && adventurousAdd !== '') {
      return `${covidAdd} , ${adventurousAdd}`;
    } else if (covidAdd !== '') {
      return `${covidAdd}`;
    } else if (adventurousAdd !== '') {
      return `${adventurousAdd}`;
    } else {
      return 'NA';
    }
  };

  const initialPlan = () =>
    createProductItem(
      `${itemName}_${planClass?.description.toLowerCase()} `,
      `${productName} ${capitalizeFirstLetterOfEachWord(productType)}`,
      `Travel ${capitalizeFirstLetterOfEachWord(productType)}`,
      `${tripType}`,
      `${destination}`,
      `${planType}`,
      `${handleAddon()}`,
      `${planClass?.description}`,
      'MYR',
      `${planPrice?.netPremiumRate}`,
      '1',
    );

  const optionalAddOn = (planAndAddonDetails: any) => {
    if (covid19.isSelected) {
      const addonCovid19 = createProductItem(
        `${itemName}_${uiState.productAddonRate[0].addOnCode.toLowerCase()}`,
        `${capitalizeFirstLetterOfEachWord(covidData.label)}`,
        `Travel ${capitalizeFirstLetterOfEachWord(productType)}`,
        `${tripType}`,
        `${destination}`,
        `${planType}`,
        `${covidAdd}`,
        `${planClass?.description}`,
        'MYR',
        `${uiState.productAddonRate[0].netAddOnRate}`,
        '1',
      );
      planAndAddonDetails.push(addonCovid19);
    }
    if (adventurousActivities.isSelected) {
      const addon = uiState.productAddonRate?.find(
        (item) => item.planClass === planSummary.planClass,
      );
      const addonActivity = createProductItem(
        `${itemName}_${addon?.addOnCode.toLowerCase()}`,
        `${capitalizeFirstLetterOfEachWord(adventurousData.gaLabel)}`,
        `Travel ${capitalizeFirstLetterOfEachWord(productType)}`,
        `${tripType}`,
        `${destination}`,
        `${planType}`,
        `${adventurousAdd}`,
        `${planClass?.description}`,
        'MYR',
        `${addon?.netAddOnRate}`,
        '1',
      );
      planAndAddonDetails.push(addonActivity);
    }
  };

  // #endregion

  // #region Quotation

  const requestQuotation = (
    destinationDescription: string,
    travelTypeDescription: string,
    manDays: number,
  ) => {
    if (!isEnableGA[countryCode]) return;

    googleAnalyticsQuoteButton(
      `${productType}_request_quote`,
      `${productName} - ${capitalizeFirstChar(productType)}`,
      `Request ${capitalizeFirstChar(productType)} Quotation`,
      `${capitalizeFirstChar(productType)} Quotation`,
      `${capitalizeFirstLetterOfEachWord(destinationDescription)}`,
      `${capitalizeFirstLetterOfEachWord(travelTypeDescription)}`,
      manDays,
    );
  };

  const expandFAQ = (title: string) => {
    if (!isEnableGA[countryCode]) return;

    googleAnalyticsNormalAction(
      'expand_section',
      `${productName} - ${capitalizeFirstChar(productType)}`,
      'Expand Section',
      `FAQ - ${capitalizeFirstLetterOfEachWord(title)}`,
    );
  };

  const moreInfoFAQ = () => {
    if (!isEnableGA[countryCode]) return;

    googleAnalyticsNormalAction(
      'go_to_page',
      `${productName} - ${capitalizeFirstChar(productType)}`,
      'Go To Page',
      `More Information - ${capitalizeFirstChar(productType)}`,
    );
  };

  // #endregion

  // #region Plan

  const viewItemPlan = () => {
    if (!isEnableGA[countryCode]) return;

    const planAndAddonDetails = [];
    const addon = uiState.productAddonRate?.find(
      (item) => item.planClass === planSummary.planClass,
    );
    const covidPrice = covid19.isSelected
      ? uiState.productAddonRate[0].netAddOnRate.toFixed(2)
      : '0.00';
    const adventurousPrice = adventurousActivities.isSelected
      ? addon?.netAddOnRate.toFixed(2)
      : '0.00';
    const totalAllPrice = (
      planPrice?.netPremiumRate +
      parseFloat(covidPrice) +
      parseFloat(adventurousPrice)
    ).toFixed(2);

    planAndAddonDetails.push(initialPlan());
    optionalAddOn(planAndAddonDetails);
    googleAnalyticsWithQuotationDetails(
      'view_item',
      `${productName} - ${capitalizeFirstChar(
        productType,
      )} - ${capitalizeFirstLetterOfEachWord(planType)}`,
      'View Item',
      `${productName} ${capitalizeFirstLetterOfEachWord(productType)}`,
      'MYR',
      `${totalAllPrice}`,
      planAndAddonDetails,
    );
  };

  const purchasePlan = () => {
    if (!isEnableGA[countryCode]) return;

    const planAndAddonDetails = [];
    planAndAddonDetails.push(initialPlan());
    optionalAddOn(planAndAddonDetails);
    googleAnalyticsWithQuotationDetails(
      'add_to_cart',
      `${productName} - ${capitalizeFirstChar(
        productType,
      )} - ${capitalizeFirstLetterOfEachWord(planType)}`,
      'Add To Cart',
      `${productName} ${capitalizeFirstLetterOfEachWord(productType)}`,
      'MYR',
      `${totalPrices}`,
      planAndAddonDetails,
    );
  };

  const goToGeneralExclusion = () => {
    if (!isEnableGA[countryCode]) return;

    googleAnalyticsNormalAction(
      'go_to_page',
      `${productName} - ${capitalizeFirstChar(
        productType,
      )} - ${capitalizeFirstLetterOfEachWord(planType)}`,
      'Go To Page',
      'General Exclusion',
    );
  };

  // #endregion

  // #region Application

  const submitPersonalDetails = () => {
    if (!isEnableGA[countryCode]) return;

    googleAnalyticsNormalAction(
      `${productType}_submit_personal_details`,
      `${productName} - ${capitalizeFirstChar(
        productType,
      )} - ${capitalizeFirstLetterOfEachWord(planType)}`,
      'Submit Personal Details',
      'Success',
    );
  };

  const submitSpouseDetails = () => {
    if (!isEnableGA[countryCode]) return;

    googleAnalyticsNormalAction(
      `${productType}_submit_spouse_details`,
      `${productName} - ${capitalizeFirstChar(
        productType,
      )} - ${capitalizeFirstLetterOfEachWord(planType)}`,
      'Submit Spouse Details',
      'Success',
    );
  };

  const submitChildDetails = () => {
    if (!isEnableGA[countryCode]) return;

    googleAnalyticsNormalAction(
      `${productType}_submit_child_details`,
      `${productName} - ${capitalizeFirstChar(
        productType,
      )} - ${capitalizeFirstLetterOfEachWord(planType)}`,
      'Submit Child Details',
      'Success',
    );
  };

  const submitApplicationForm = () => {
    if (!isEnableGA[countryCode]) return;

    const planAndAddonDetails = [];
    const email = personalDetails.adultDetails[0].email;
    const mobileNumber = personalDetails.adultDetails[0].mobileNumber;
    planAndAddonDetails.push(initialPlan());
    optionalAddOn(planAndAddonDetails);
    googleAnalyticsWithEmailAndMobile(
      'begin_checkout',
      `${productName} - ${capitalizeFirstChar(
        productType,
      )} - ${capitalizeFirstLetterOfEachWord(planType)}`,
      'Begin Checkout',
      `${productName} ${capitalizeFirstLetterOfEachWord(productType)}`,
      `${email}`,
      `${mobileNumber}`,
      'MYR',
      `${totalPrices}`,
      planAndAddonDetails,
    );
  };

  // #endregion

  // #region Review

  const reviewDeclaration = (name: string, action: string) => {
    if (!isEnableGA[countryCode]) return;

    googleAnalyticsNormalAction(
      `${name}`,
      `${productName} - ${capitalizeFirstChar(
        productType,
      )} - ${capitalizeFirstLetterOfEachWord(planType)}`,
      `${action}`,
      'Success',
    );
  };

  // #endregion

  // #region Payment

  const proceedPayment = () => {
    if (!isEnableGA[countryCode]) return;

    const planAndAddonDetails = [];
    const email = personalDetails.adultDetails[0].email;
    const mobileNumber = personalDetails.adultDetails[0].mobileNumber;

    const setCookieWithExpiration = (
      cookieName: string,
      data: any,
      minutes: number,
    ) => {
      // Serialize the data to JSON
      const serializedData = JSON.stringify(data);

      // Calculate the expiration time
      const expirationTime = new Date(
        new Date().getTime() + minutes * 60 * 1000,
      ); // Calculate expiration time in milliseconds

      // Use Cookies.set to set the cookie with an expiration time
      Cookies.set(cookieName, serializedData, {
        expires: expirationTime,
        path: '/',
      });
    };

    planAndAddonDetails.push(initialPlan());
    optionalAddOn(planAndAddonDetails);
    googleAnalyticsWithEmailAndMobile(
      'add_payment_info',
      `${productName} - ${capitalizeFirstChar(
        productType,
      )} - ${capitalizeFirstLetterOfEachWord(planType)}`,
      'Add Payment Info',
      `${productName} ${capitalizeFirstLetterOfEachWord(productType)}`,
      `${email}`,
      `${mobileNumber}`,
      'MYR',
      `${totalPrices}`,
      planAndAddonDetails,
    );

    const modifiedArray = planAndAddonDetails.map((item) => ({
      ...item,
      totalPrice: totalPrices,
      email,
      mobileNumber,
    }));
    // Usage: Set a cookie named 'myCookie' with data and an expiration time of 15 minutes
    setCookieWithExpiration('myCookie', modifiedArray, 15);
  };

  const successPayment = (transactionNo: string, policyNumber: any) => {
    if (!isEnableGA[countryCode]) return;

    if (myCookieArray !== null) {
      const totalPrices = myCookieArray[0].totalPrice;
      const withoutTotalPrice = myCookieArray;
      delete withoutTotalPrice[0].totalPrice;
      const email = withoutTotalPrice[0].email;
      const mobileNumber = withoutTotalPrice[0].mobileNumber;
      delete withoutTotalPrice[0].email;
      delete withoutTotalPrice[0].mobileNumber;
      const policyNo = policyNumber[0];
      const updatePolicyNoArray = withoutTotalPrice.map((item) => {
        return {
          ...item,
          epolicy_number: policyNo,
        };
      });
      googleAnalyticsWithEmailAndMobile(
        'purchase',
        `${productName} - ${capitalizeFirstChar(
          productType,
        )} - ${capitalizeFirstLetterOfEachWord(
          myCookieArray[0]?.item_category4,
        )}`,
        'Purchase',
        `${productName} ${capitalizeFirstLetterOfEachWord(productType)}`,
        `${email}`,
        `${mobileNumber}`,
        'MYR',
        `${totalPrices}`,
        updatePolicyNoArray,
        transactionNo,
      );
    }
  };

  const retryPayment = () => {
    if (!isEnableGA[countryCode]) return;

    if (myCookieArray !== null) {
      const totalPrices = myCookieArray[0].totalPrice;
      const withoutTotalPrice = myCookieArray;
      delete withoutTotalPrice[0].totalPrice;
      const email = withoutTotalPrice[0].email;
      const mobileNumber = withoutTotalPrice[0].mobileNumber;
      delete withoutTotalPrice[0].email;
      delete withoutTotalPrice[0].mobileNumber;
      googleAnalyticsWithEmailAndMobile(
        'add_payment_info',
        `${productName} - ${capitalizeFirstChar(
          productType,
        )} - ${capitalizeFirstLetterOfEachWord(
          myCookieArray[0].item_category4,
        )}`,
        'Add Payment Info',
        `${productName} ${capitalizeFirstLetterOfEachWord(productType)}`,
        `${email}`,
        `${mobileNumber}`,
        'MYR',
        `${totalPrices}`,
        withoutTotalPrice,
      );
    }
  };

  const failedPayment = () => {
    if (!isEnableGA[countryCode]) return;

    if (myCookieArray !== null) {
      googleAnalyticsNormalAction(
        'purchase_failed',
        `${productName} - ${capitalizeFirstChar(
          productType,
        )} - ${capitalizeFirstLetterOfEachWord(
          myCookieArray[0]?.item_category4,
        )}`,
        'Purchase Failed',
        'Failed Purchase',
      );
    }
  };

  const failedTechnicalIssuePayment = () => {
    if (!isEnableGA[countryCode]) return;

    googleAnalyticsNormalAction(
      'purchase_failed',
      `${productName} - ${capitalizeFirstChar(
        productType,
      )} - ${capitalizeFirstLetterOfEachWord(planType)}`,
      'Purchase Failed',
      'Failed Purchase - Technical Issue',
    );
  };

  // #endregion

  return {
    requestQuotation,
    expandFAQ,
    moreInfoFAQ,
    viewItemPlan,
    purchasePlan,
    goToGeneralExclusion,
    submitPersonalDetails,
    submitSpouseDetails,
    submitChildDetails,
    submitApplicationForm,
    reviewDeclaration,
    proceedPayment,
    successPayment,
    retryPayment,
    failedPayment,
    failedTechnicalIssuePayment,
  };
}
